import { Outlet, NavLink } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useTenant } from '../hooks/useTenant';
import { useDateRange, PRESETS, type DatePreset } from '../hooks/useDateRange';
import { useResponsive } from '../hooks/useResponsive';
import { useEffect, useState } from 'react';
import {
  LayoutDashboard, GitFork, MapPin, BarChart2, Database,
  Crown, Settings, LogOut, Calendar, Menu, X, Building2, ChevronDown, Layers
} from 'lucide-react';

const navItems = [
  { to: '/dashboard/overview',     label: 'Overview',          Icon: LayoutDashboard },
  { to: '/dashboard/flows',        label: 'Visitor Flows',     Icon: GitFork },
  { to: '/dashboard/attractions',  label: 'Attrazioni',        Icon: MapPin },
  { to: '/dashboard/analytics',    label: 'Analytics',         Icon: BarChart2 },
  { to: '/dashboard/data-sources', label: 'Data Sources',      Icon: Database },
  { to: '/dashboard/pois',         label: 'POI Settings',      Icon: Layers },
];

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const { selectedTenantId, setSelectedTenantId, tenants, fetchTenants } = useTenant();
  const { preset, label, setPreset } = useDateRange();
  const responsive = useResponsive();
  const [sidebarOpen, setSidebarOpen] = useState(!responsive.isSmall);

  // Close sidebar when screen size changes to small
  useEffect(() => {
    if (responsive.isSmall) {
      setSidebarOpen(false);
    } else {
      setSidebarOpen(true);
    }
  }, [responsive.isSmall]);

  // Load tenants and set default tenant on mount
  useEffect(() => {
    if (!user) return;
    if (user.role === 'superadmin') {
      // Always fetch tenants for superadmin (needed to display names)
      fetchTenants();
    } else if (user.tenantId) {
      // For admin/operatore: always force their own tenantId, overriding any leftover superadmin selection
      setSelectedTenantId(user.tenantId);
    }
  }, [user]);

  // Get current tenant name
  const currentTenant = tenants.find(t => t.id === selectedTenantId);
  const tenantDisplayName = currentTenant?.name || 'No Tenant Selected';

  return (
    <div className="flex h-screen bg-white font-sans">
      {/* Mobile overlay */}
      {responsive.isSmall && sidebarOpen && (
        <div
          onClick={() => setSidebarOpen(false)}
          className="fixed inset-0 bg-black/40 z-[99] backdrop-blur-sm"
        />
      )}

      {/* Sidebar */}
      <aside
        className={`${sidebarOpen ? 'w-64' : 'w-0'} bg-gradient-to-b from-mp-navy to-mp-sky flex flex-col py-6 transition-all duration-300 ease-in-out ${
          responsive.isSmall ? 'fixed h-screen z-100 overflow-y-auto shadow-2xl' : 'static'
        }`}
      >
        {/* Logo */}
        <div className="px-6 pb-8">
          <div className="flex items-center gap-3">
            <div className="relative w-10 h-10 rounded-xl bg-gradient-to-br from-mp-accent to-orange-500 flex items-center justify-center shadow-lg">
              <svg width="24" height="24" viewBox="0 0 32 32" fill="none">
                <circle cx="16" cy="16" r="16" fill="#fff" opacity="0.15"/>
                <path d="M16 6C12.134 6 9 9.134 9 13C9 18.25 16 26 16 26C16 26 23 18.25 23 13C23 9.134 19.866 6 16 6Z" fill="#fff"/>
                <circle cx="16" cy="13" r="2.5" fill="#004E89"/>
              </svg>
            </div>
            <div>
              <div className="text-mp-white font-black text-lg tracking-tight">SATI</div>
              <div className="text-mp-accent text-xs font-bold">INTELLIGENCE</div>
            </div>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-3 flex flex-col gap-2 overflow-y-auto">
          {navItems.map(({ to, label, Icon }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) => `flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-sm font-bold transition-all duration-200 ${
                isActive
                  ? 'bg-mp-accent text-mp-white border-l-4 border-orange-400'
                  : 'text-mp-gray-200 border-l-4 border-transparent hover:bg-mp-sky/50 hover:text-mp-accent'
              }`}
            >
              {({ isActive }) => (
                <>
                  <Icon size={18} style={{ opacity: isActive ? 1 : 0.7 }} />
                  {label}
                </>
              )}
            </NavLink>
          ))}
          
          {/* Superadmin link */}
          {user?.role === 'superadmin' && (!user?.tenantId || user?.tenantId === null) && (
            <>
              <div className="h-px bg-mp-white/10 my-3" />
              <NavLink
                to="/superadmin"
                className={({ isActive }) => `flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-sm font-bold transition-all duration-200 ${
                  isActive
                    ? 'bg-mp-accent text-mp-white border-l-4 border-orange-400'
                    : 'text-mp-gray-200 border-l-4 border-transparent hover:bg-mp-sky/50 hover:text-mp-accent'
                }`}
              >
                {({ isActive }) => (
                  <>
                    <Crown size={18} style={{ opacity: isActive ? 1 : 0.7 }} />
                    Platform Admin
                  </>
                )}
              </NavLink>
            </>
          )}

          {/* Admin link */}
          {user?.role === 'admin' && user?.tenantId && (
            <>
              <div className="h-px bg-mp-white/10 my-3" />
              <NavLink
                to="/admin"
                className={({ isActive }) => `flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-sm font-bold transition-all duration-200 ${
                  isActive
                    ? 'bg-mp-accent text-mp-white border-l-4 border-orange-400'
                    : 'text-mp-gray-200 border-l-4 border-transparent hover:bg-mp-sky/50 hover:text-mp-accent'
                }`}
              >
                {({ isActive }) => (
                  <>
                    <Settings size={18} style={{ opacity: isActive ? 1 : 0.7 }} />
                    Admin Panel
                  </>
                )}
              </NavLink>
            </>
          )}
        </nav>

        {/* User */}
        <div className="px-3 pt-3 border-t border-mp-white/10">
          <div className="flex items-center gap-3 p-3 rounded-lg bg-mp-white/10 hover:bg-mp-white/20 transition-colors mb-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-mp-accent to-orange-500 flex items-center justify-center text-mp-white text-sm font-black shadow-md">
              {user?.email?.[0]?.toUpperCase() ?? 'U'}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-mp-white text-sm font-bold truncate">{user?.name || user?.email}</div>
              <div className="text-mp-gray-300 text-xs font-semibold capitalize">{user?.role}</div>
            </div>
          </div>
          <button
            onClick={logout}
            className="w-full px-3 py-2.5 bg-mp-white/10 hover:bg-mp-white/20 border-2 border-mp-accent rounded-lg text-mp-accent text-sm font-bold cursor-pointer transition-all duration-200 flex items-center justify-center gap-2 hover:shadow-lg"
          >
            <LogOut size={16} />
            Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 overflow-auto bg-mp-gray-100 flex flex-col">
        {/* Top bar */}
        <div className={`px-6 py-4 border-b-2 border-mp-accent/20 bg-mp-white flex items-center justify-between gap-4 shadow-sm ${responsive.isSmall ? 'flex-wrap' : ''}`}>
          {/* Mobile menu toggle */}
          {responsive.isSmall && (
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2.5 bg-mp-gray-100 hover:bg-mp-gray-200 rounded-lg text-mp-navy transition-colors"
            >
              {sidebarOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          )}

          {/* Tenant Selector - Always show for superadmin */}
          {user?.role === 'superadmin' && (!user?.tenantId || user?.tenantId === null) ? (
            <div className={`relative flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-mp-navy/5 to-mp-accent/5 border-2 border-mp-accent/40 rounded-xl hover:border-mp-accent/70 hover:shadow-lg transition-all flex-1 max-w-md cursor-pointer group ${responsive.isSmall ? 'order-3 w-full max-w-full' : ''}`}>
              {/* Invisible select covers entire container */}
              <select
                title="Select tenant"
                value={selectedTenantId || ''}
                onChange={(e) => setSelectedTenantId(e.target.value)}
                className="absolute inset-0 opacity-0 cursor-pointer w-full h-full z-10"
              >
                <option value="">Select Tenant...</option>
                {tenants.length > 0 ? (
                  tenants.map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))
                ) : (
                  <option disabled>Loading tenants...</option>
                )}
              </select>
              <Building2 size={18} className="text-mp-accent flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold text-mp-gray-600 uppercase tracking-wider">Active Tenant</div>
                <div className="text-sm font-black text-mp-navy truncate">{tenantDisplayName}</div>
              </div>
              <ChevronDown size={18} className="text-mp-accent group-hover:text-mp-navy transition-colors flex-shrink-0" />
            </div>
          ) : null}

          {/* Spacer for centering */}
          <div className="flex-1 min-w-0"></div>

          {/* Date range + Info on right */}
          <div className="flex items-center gap-6 flex-shrink-0">
            {/* Date range selector */}
            <div className="flex items-center gap-2 bg-mp-white border-2 border-mp-accent/30 rounded-lg p-1.5 flex-shrink-0 hover:bg-mp-accent/5 transition-colors">
              <Calendar size={16} className="text-mp-gray-500 ml-2" />
              {(Object.keys(PRESETS) as DatePreset[]).map((p) => (
                <button
                  key={p}
                  onClick={() => setPreset(p)}
                  className={`px-3 py-1.5 rounded-md text-sm font-bold transition-all duration-200 whitespace-nowrap ${
                    preset === p
                      ? 'bg-mp-accent text-mp-white shadow-md'
                      : 'bg-transparent text-mp-gray-600 hover:bg-mp-accent/10'
                  }`}
                >
                  {PRESETS[p].label}
                </button>
              ))}
            </div>

            {!responsive.isSmall && (
              <div className="text-xs font-bold text-mp-gray-600">
                {new Date().toLocaleDateString('en-GB', { weekday: 'short', month: 'short', day: 'numeric' })}
              </div>
            )}
            <div className="flex items-center gap-2 px-3 py-1.5 bg-mp-accent/10 rounded-full border-2 border-mp-accent/50 shadow-sm">
              <div className="w-2 h-2 bg-mp-accent rounded-full animate-pulse"></div>
              <span className="text-xs font-bold text-mp-accent">
                Live{!responsive.isSmall && ` · ${label}`}
              </span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className={`flex-1 overflow-auto ${responsive.isSmall ? 'px-4 py-4' : 'px-8 py-6'}`}>
          <Outlet />
        </div>
      </main>
    </div>
  );
}
